import {
  Alert,
  App,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { suggestBarrierWaypoints } from '@/api/endpoints/barriers';
import { useBarrierMutations } from '@/hooks/useBarrierMutations';
import {
  useBarrierWorkspace,
  type BarrierDraft,
} from '@/stores/BarrierWorkspaceContext';
import { useMapTool } from '@/stores/MapToolContext';
import { useRecomputeProgress } from '@/stores/RecomputeProgressContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { BarrierDirection, BarrierRequest, LatLonPoint } from '@/types/barriers';
import { extractAxiosMessage } from '@/utils/axiosError';
import { notifyRecomputeEnqueued } from '@/utils/recomputeNotify';

import DirectionSelector from './DirectionSelector';
import WaypointsEditor from './WaypointsEditor';

interface FormValues {
  readonly name: string;
  readonly comment: string;
  readonly radius_m: number;
}

const LAT_LON_DECIMALS = 5;
const MIN_RADIUS_M = 1;
const MAX_RADIUS_M = 5000;
const MAX_NAME_LEN = 200;
const MAX_COMMENT_LEN = 2000;

interface BarrierCreateFormProps {
  readonly onSaved?: (barrierId: number) => void;
}

export default function BarrierCreateForm({ onSaved }: BarrierCreateFormProps) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const {
    draft,
    cancelDraft,
    updateDraftFields,
    addDraftWaypoints,
  } = useBarrierWorkspace();
  const { mode, setMode } = useMapTool();
  const { bumpBarriers } = useTileRevision();
  const { notifyEnqueued } = useRecomputeProgress();
  const { saving, create } = useBarrierMutations();
  const [form] = Form.useForm<FormValues>();
  const [suggesting, setSuggesting] = useState(false);
  // Sequence counter prevents stale responses from overwriting fresh ones
  // when the user clicks "Suggest" multiple times in quick succession.
  const suggestSeqRef = useRef(0);

  const handleSuggest = useCallback(async () => {
    if (!draft) {
      return;
    }
    const mySeq = ++suggestSeqRef.current;
    setSuggesting(true);
    try {
      const response = await suggestBarrierWaypoints({
        point: draft.point,
        radius_m: draft.radius_m,
      });
      if (suggestSeqRef.current !== mySeq) {
        return;
      }
      if (response.items.length === 0) {
        notification.info({
          message: t('barriers.waypoints.suggestEmptyTitle'),
          description: t('barriers.waypoints.suggestEmptyDescription'),
        });
        return;
      }
      // Auto-suggest writes directly to auto_waypoints — the candidate
      // preview step (set → review → accept) was removed in favour of a
      // single click that produces the finished detour.
      addDraftWaypoints(
        response.items.map((item) => ({ lat: item.lat, lon: item.lon })),
      );
    } catch (error) {
      if (suggestSeqRef.current !== mySeq) {
        return;
      }
      notification.error({
        message: t('barriers.waypoints.suggestErrorTitle'),
        description: extractAxiosMessage(error) ?? t('errors.unexpected'),
      });
    } finally {
      if (suggestSeqRef.current === mySeq) {
        setSuggesting(false);
      }
    }
  }, [addDraftWaypoints, draft, notification, t]);

  useEffect(() => {
    if (!draft) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      name: draft.name,
      comment: draft.comment,
      radius_m: draft.radius_m,
    });
  }, [draft, form]);

  if (!draft) {
    return null;
  }

  const handleValuesChange = (_changed: Partial<FormValues>, values: FormValues) => {
    updateDraftFields({
      name: values.name,
      comment: values.comment,
      radius_m: values.radius_m,
    });
  };

  const handleDirectionChange = (direction: BarrierDirection) => {
    updateDraftFields({
      direction,
      direction_hint: direction === 'both' ? null : draft.direction_hint,
    });
    if (direction === 'both' && mode === 'pick_direction_hint') {
      setMode('create_barrier');
    }
  };

  const handlePickHint = () => setMode('pick_direction_hint');
  const handleStartAddWaypoint = () => setMode('add_waypoint');

  const hintReady = draft.direction === 'both' || draft.direction_hint?.length === 2;
  const canSave = Boolean(hintReady) && draft.name.trim().length > 0;

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if (!canSave) {
      return;
    }
    const body: BarrierRequest = toRequest(draft);
    try {
      const created = await create(body);
      cancelDraft();
      bumpBarriers();
      setMode('select');
      notifyEnqueued(created.marked_stale);
      notifyRecomputeEnqueued(message, t, created.marked_stale);
      onSaved?.(created.barrier.id);
    } catch (error) {
      notification.error({
        message: t('barriers.create.saveErrorTitle'),
        description: extractAxiosMessage(error) ?? t('errors.unexpected'),
      });
    }
  };

  const handleCancel = () => {
    cancelDraft();
    setMode('select');
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={formatPoint(draft.point, draft.pointLabel, t)}
      />

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        initialValues={{
          name: draft.name,
          comment: draft.comment,
          radius_m: draft.radius_m,
        }}
      >
        <Form.Item
          name="name"
          label={t('barriers.fields.name')}
          rules={[{ required: true, message: t('barriers.validation.nameRequired') }]}
        >
          <Input autoFocus maxLength={MAX_NAME_LEN} />
        </Form.Item>

        <Form.Item name="comment" label={t('barriers.fields.comment')}>
          <Input.TextArea rows={2} maxLength={MAX_COMMENT_LEN} />
        </Form.Item>

        <Form.Item
          name="radius_m"
          label={t('barriers.fields.radiusM')}
          rules={[{
            required: true,
            type: 'number',
            min: MIN_RADIUS_M,
            max: MAX_RADIUS_M,
            message: t('barriers.validation.radiusRange', {
              min: MIN_RADIUS_M,
              max: MAX_RADIUS_M,
            }),
          }]}
        >
          <InputNumber
            min={MIN_RADIUS_M}
            max={MAX_RADIUS_M}
            step={10}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>

      <div>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          {t('barriers.fields.direction')}
        </Typography.Text>
        <DirectionSelector
          value={draft.direction}
          hintSet={draft.direction_hint?.length === 2}
          onChange={handleDirectionChange}
          onPickHint={handlePickHint}
          pickingHint={mode === 'pick_direction_hint'}
        />
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <WaypointsEditor
        waypoints={draft.auto_waypoints}
        addingWaypoint={mode === 'add_waypoint'}
        suggesting={suggesting}
        canSuggest={draft.radius_m > 0 && !suggesting}
        onStartAdd={handleStartAddWaypoint}
        onRemove={(index) => {
          const next = draft.auto_waypoints.filter((_, i) => i !== index);
          updateDraftFields({ auto_waypoints: next });
        }}
        onSuggest={handleSuggest}
      />

      <Divider style={{ margin: '8px 0' }} />

      <Space>
        <Button
          type="primary"
          loading={saving}
          disabled={!canSave}
          onClick={handleSubmit}
        >
          {t('common.save')}
        </Button>
        <Button onClick={handleCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
      </Space>
    </Space>
  );
}

function toRequest(draft: BarrierDraft): BarrierRequest {
  return {
    name: draft.name.trim(),
    comment: draft.comment.trim().length > 0 ? draft.comment.trim() : null,
    point: draft.point,
    radius_m: draft.radius_m,
    direction: draft.direction,
    direction_hint: draft.direction === 'both' ? null : (draft.direction_hint ?? null),
    auto_waypoints: draft.auto_waypoints,
  };
}

function formatPoint(
  point: LatLonPoint,
  roadName: string | null,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const coords = `${point.lat.toFixed(LAT_LON_DECIMALS)}, ${point.lon.toFixed(LAT_LON_DECIMALS)}`;
  if (roadName) {
    return t('barriers.create.snappedTo', { road: roadName, coords });
  }
  return t('barriers.create.snappedGeneric', { coords });
}
